import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { Logo } from "@/components/Logo";
import { api } from "@/lib/api";

const Shell = ({ children }) => (
  <div className="min-h-screen bg-obsidian bg-grid flex items-center justify-center px-4">
    <div className="glass-card rounded-2xl p-10 w-full max-w-md text-center">
      <div className="flex justify-center mb-8">
        <Logo />
      </div>
      {children}
    </div>
  </div>
);

export const PaymentSuccess = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const sumupRef = params.get("sumup_ref");
  const [state, setState] = useState("checking");

  useEffect(() => {
    if (!sessionId && !sumupRef) {
      setState("error");
      return;
    }
    let tries = 0;
    const poll = async () => {
      try {
        const { data } = sessionId
          ? await api.get(`/payments/status/${sessionId}`)
          : await api.get(`/payments/sumup/status/${sumupRef}`);
        if (data.payment_status === "paid" || data.status === "PAID") {
          setState("paid");
          return;
        }
        if (data.status === "FAILED" || data.status === "EXPIRED") {
          setState("error");
          return;
        }
      } catch {
        /* continua il polling */
      }
      tries += 1;
      if (tries < 15) setTimeout(poll, 2000);
      else setState("timeout");
    };
    poll();
  }, [sessionId, sumupRef]);

  return (
    <Shell>
      {state === "checking" && (
        <div data-testid="payment-status-checking">
          <Loader2 size={40} className="text-volt animate-spin mx-auto" />
          <p className="mt-5 font-mono text-xs uppercase tracking-widest text-slate-400">Verifica pagamento in corso...</p>
        </div>
      )}
      {state === "paid" && (
        <div data-testid="payment-status-paid">
          <CheckCircle2 size={48} className="text-emerald-400 mx-auto" />
          <h1 className="font-display text-2xl font-bold text-white mt-5">Consulenza confermata!</h1>
          <p className="mt-3 text-sm text-slate-400 leading-relaxed">
            Pagamento ricevuto. Ti abbiamo inviato una email con i dettagli del collegamento.
            Ci vediamo all'orario prenotato.
          </p>
        </div>
      )}
      {(state === "timeout" || state === "error") && (
        <div data-testid="payment-status-pending">
          <Loader2 size={40} className="text-spark animate-pulse mx-auto" />
          <h1 className="font-display text-xl font-bold text-white mt-5">Verifica in corso</h1>
          <p className="mt-3 text-sm text-slate-400">
            Stiamo ancora attendendo la conferma del pagamento. Riceverai una email appena completato.
          </p>
        </div>
      )}
      <div className="flex flex-col sm:flex-row gap-3 justify-center mt-8">
        <Link
          to="/dashboard"
          data-testid="payment-success-dashboard-link"
          className="inline-block bg-volt text-obsidian font-semibold px-6 py-3 rounded-full hover:bg-spark transition-colors"
        >
          Vai alla Dashboard
        </Link>
        <Link
          to="/"
          data-testid="payment-success-home-link"
          className="inline-block border border-white/20 text-slate-200 px-6 py-3 rounded-full hover:border-volt hover:text-volt transition-colors"
        >
          Torna alla Home
        </Link>
      </div>
    </Shell>
  );
};

export const PaymentCancel = () => (
  <Shell>
    <div data-testid="payment-cancel-message">
      <XCircle size={48} className="text-spark mx-auto" />
      <h1 className="font-display text-2xl font-bold text-white mt-5">Pagamento annullato</h1>
      <p className="mt-3 text-sm text-slate-400 leading-relaxed">
        Nessun addebito è stato effettuato. Puoi riprovare quando vuoi: lo slot resta
        riservato solo a pagamento completato.
      </p>
    </div>
    <Link
      to="/"
      data-testid="payment-cancel-home-link"
      className="mt-8 inline-block border border-white/20 text-slate-200 px-6 py-3 rounded-full hover:border-volt hover:text-volt transition-colors"
    >
      Torna alla Home
    </Link>
  </Shell>
);
